/** **Import page…** on the workspace home (spec §8.20): owners and editors upload a `.kss.zip` archive. */
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { qk } from "../api/hooks";
import { importPage, type ImportResult } from "./api";
import { useT } from "../i18n";

export function ImportPageButton({ workspaceId }: { workspaceId: string }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [phase, setPhase] = useState<"idle" | "uploading" | "importing" | "done" | "failed">("idle");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async (file: File) => {
    setResult(null);
    setError(null);
    setProgress(0);
    setPhase("uploading");
    try {
      const job = await importPage(workspaceId, file, (f) => {
        setProgress(f);
        if (f >= 1) setPhase("importing");
      });
      setResult(job.result as unknown as ImportResult);
      setPhase("done");
      await qc.invalidateQueries({ queryKey: qk.pages(workspaceId) });
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^\d+: /, "") : String(e));
      setPhase("failed");
    }
  };

  const busy = phase === "uploading" || phase === "importing";
  return (
    <div className="mb-4 text-sm" data-testid="import-page" data-phase={phase}>
      <input
        ref={input}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        data-testid="import-page-file"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void start(f);
        }}
      />
      <button
        type="button"
        className="flex items-center gap-1 rounded border border-neutral-300 px-2 py-1 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
        disabled={busy || !navigator.onLine}
        title={navigator.onLine ? t("import.title") : t("import.offline")}
        onClick={() => input.current?.click()}
        data-testid="import-page-button"
      >
        <Upload size={14} /> {phase === "uploading" ? t("import.uploading", { percent: Math.round(progress * 100) }) : phase === "importing" ? t("import.importing") : t("import.button")}
      </button>
      {phase === "failed" && error && (
        <p className="mt-2 text-red-700" role="alert" data-testid="import-page-error">
          {/* the client's own reasons are keys; the server's pass through `t` unchanged */}
          {t("import.refused", { error: t(error) })}
        </p>
      )}
      {phase === "done" && result && (
        <div className="mt-2 rounded-md border border-neutral-200 p-2.5 dark:border-neutral-800" data-testid="import-page-result">
          <p>
            {t("import.summary", {
              title: result.title || t("page.untitled"),
              blocks: t("import.blocks", { count: result.blocks }),
              files: t("import.files", { count: result.files }),
              matched: result.concepts.matched,
              created: result.concepts.created,
              sequences: t("import.sequences", { count: result.sequences }),
              links: t("import.links", { count: result.workspaceLinks }),
            })}{" "}
            <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: result.docId }} className="text-accent underline" data-testid="import-page-open">
              {t("import.open")}
            </Link>
          </p>
          {result.notes.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-neutral-500">
              {result.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
